import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch } from "react-redux";

import moment from 'moment';

import allActions from '../../actions';
import { ClientMigrationAPI } from '../../API/client_migration';
import { myLabelInput, unoTheme } from '../../assets/styles/unoTheme';
import MyContentLoadModal from '../utils/MyContentLoadModal';
import MyDatePicker from '../utils/MyDatePicker';
import UnoButton from '../utils/UnoButton';

const useStyles = makeStyles((theme) => ({
    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingBottom: theme.spacing(2),
        minHeight: '320px',
    },

    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between'
        }
    },

    label: myLabelInput,

    green: {
        color: unoTheme.greenMoney,
    },
    warningColor: {
        color: unoTheme.warningColor
    },

    sourceBox: {
        background: unoTheme.secondaryTuplaTable,
        borderRadius: '5px',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        marginBottom: theme.spacing(2),
    },

    infoLine: {
        fontSize: '13px',
        color: unoTheme.defaultTextColor,
    },

    infoLineStrong: {
        fontSize: '13px',
        color: 'black',
        fontWeight: 'bold',
    },

    hint: {
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        marginTop: theme.spacing(1),
    },

    validateMessage: {
        color: unoTheme.redMoney,
        textAlign: 'center',
        padding: '16px',
        backgroundColor: '#eeeeee',
        borderRadius: '5px',
        marginTop: theme.spacing(2),
    },

    cloneList: {
        margin: '0px',
        paddingLeft: theme.spacing(3),
        '& li': {
            fontSize: '12px',
            color: unoTheme.defaultTextColor,
        }
    },

}));

const CLONED_ITEMS = [
    'Dados cadastrais, endereço, segregação e classificação',
    'Credenciais CADPREV e GESCON',
    'Logo do cliente',
    'Vínculos de assessor e dos usuários do RPPS',
    'Descrições de APR e dados para emissão de APR',
    'Suitability, política de investimentos e meta atuarial',
    'Aquisição eletrônica de títulos (enquadramento)',
];

const NOT_CLONED_ITEMS = [
    'Contas, ativos, títulos, saldos e movimentações — vêm depois pela importação de lançamentos',
    'Rentabilidades e enquadramento de risco processados — são regerados ao fechar os meses',
];

/**
 * Duplica o cadastro de um cliente com início de carteira anterior.
 *
 * O cadastro novo nasce sem nenhum mês fechado e marcado como "em migração", então fica
 * oculto para os usuários do RPPS enquanto os períodos antigos são lançados e o histórico é
 * importado do cadastro atual.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object} props.sourceClient cliente que será duplicado
 * @param {function} props.handleClose
 * @param {function} props.handleDuplicated chamado após a duplicação, para recarregar a lista
 */
export default function DuplicateClientForMigrationModal({ open, sourceClient, handleClose, handleDuplicated }) {

    const classes = useStyles();
    const dispatch = useDispatch();

    const [loader, setLoader] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);

    const sourceInit = useMemo(() => sourceClient && sourceClient.portfolio_init
        ? moment.utc(sourceClient.portfolio_init)
        : null, [sourceClient]);

    useEffect(() => {

        if (!open || !sourceInit) return;

        //Default: um mês antes do início atual, que é o menor recuo possível
        setSelectedDate(sourceInit.clone().subtract(1, 'month'));
        setLoader('');

    }, [open, sourceInit])

    //Só mês/ano importam: portfolio_init é lido em todo o sistema por mês/ano
    const isDateValid = useMemo(() => {

        if (!selectedDate || !sourceInit || !moment.utc(selectedDate).isValid()) return false;

        const selected = moment.utc(selectedDate);
        const selectedPeriod = (selected.year() * 100) + (selected.month() + 1);
        const sourcePeriod = (sourceInit.year() * 100) + (sourceInit.month() + 1);

        return selectedPeriod < sourcePeriod;

    }, [selectedDate, sourceInit])

    async function doDuplicate() {

        //Convenção do sistema: portfolio_init é o último dia do mês ANTERIOR ao início das operações
        const portfolioInit = moment.utc(selectedDate).endOf('month').format('DD/MM/YYYY');

        setLoader('Duplicando cadastro...');

        const response = await ClientMigrationAPI.duplicateForMigration(sourceClient.id, portfolioInit);

        setLoader('');

        if (!response.success) {

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: response.body && response.body.message
                    ? response.body.message
                    : 'Falha ao duplicar o cadastro',
            }))

            return;
        }

        const { new_client_id, hasLogo, logoCopied } = response.body;

        dispatch(allActions.mainAlertActions.setMainAlert({
            show: true,
            type: hasLogo && !logoCopied ? 'warning' : 'success',
            message: 'Cadastro duplicado com sucesso [ ID ' + new_client_id + ' ]'
                + (hasLogo && !logoCopied ? '. O logo não pôde ser copiado, faça o upload no novo cadastro.' : ''),
        }))

        handleDuplicated();
    }

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'sm'}
            open={open}
            onClose={() => handleClose()}>

            <DialogTitle className={classes.dialogTitle}>
                <span>{'Duplicar cadastro para migração'}</span>
            </DialogTitle>

            <DialogContent className={classes.contentModal}>

                {
                    loader ?
                        <MyContentLoadModal show={true} message={loader} />
                        :
                        <>
                            {
                                sourceClient ?
                                    <div className={classes.sourceBox}>
                                        <Typography className={classes.infoLineStrong}>
                                            {sourceClient.label_name + ' [ ID ' + sourceClient.id + ' ]'}
                                        </Typography>
                                        <Typography className={classes.infoLine}>
                                            {'CNPJ: ' + (sourceClient.cpf_cnpj || '-')}
                                        </Typography>
                                        <Typography className={classes.infoLine}>
                                            {'Início da carteira atual: '
                                                + (sourceInit ? sourceInit.format('MM/YYYY') : '-')
                                                + ' | Último mês fechado: '
                                                + (sourceClient.portfolio_closed ? sourceClient.portfolio_closed.trim() : '-')}
                                        </Typography>
                                    </div>
                                    : null
                            }

                            <MyDatePicker
                                views={["month", "year"]}
                                margin="dense"
                                id="date-picker-new-portfolio-init"
                                outerlabel="Novo início de carteira"
                                format="MM/yyyy"
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                            />

                            <Typography className={classes.hint}>
                                {'O início da carteira deve ser o mês anterior ao início das movimentações. '
                                    + 'O cadastro novo nasce sem nenhum mês fechado e oculto para os usuários do RPPS.'}
                            </Typography>

                            <Typography className={classes.label} style={{ marginTop: '16px' }}>
                                {'O que será replicado'}
                            </Typography>
                            <ul className={classes.cloneList}>
                                {CLONED_ITEMS.map((item, index) => (
                                    <li key={'cloned-item-' + index}>{item}</li>
                                ))}
                            </ul>

                            <Typography className={classes.label} style={{ marginTop: '12px' }}>
                                {'O que não será replicado'}
                            </Typography>
                            <ul className={classes.cloneList}>
                                {NOT_CLONED_ITEMS.map((item, index) => (
                                    <li key={'not-cloned-item-' + index}>{item}</li>
                                ))}
                            </ul>

                            {
                                !isDateValid ?
                                    <Typography variant="body2" className={classes.validateMessage}>
                                        {sourceInit
                                            ? 'O novo início de carteira deve ser anterior a ' + sourceInit.format('MM/YYYY')
                                            : 'O cliente de origem não possui início de carteira definido'}
                                    </Typography>
                                    : null
                            }
                        </>
                }

            </DialogContent>

            {
                !loader ?
                    <DialogActions className={classes.actionsFlex}>

                        <UnoButton onClick={() => handleClose()} color="secondary">
                            {'Cancelar'}
                        </UnoButton>

                        <UnoButton
                            disabled={!isDateValid}
                            onClick={() => doDuplicate()}
                            type="success">
                            {'Duplicar cadastro'}
                        </UnoButton>

                    </DialogActions>
                    : null
            }

        </Dialog>
    );
}
